<template lang="pug">
  v-container.fill-height.align-start.flex-column(fluid)
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{ snackbarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackbar = false") {{ $t('buttons.closeButtonLabel') }}
    DebtorDetailDialog(v-if="showDebtorDetailDialog" :showDebtorDetailDialog="showDebtorDetailDialog"
      @closeDebtorDetailDialog="closeDebtorDetailDialog" :debtor="selectedDebtor")
    SendEmailDialog(v-if="showSendEmailDialog" :show="showSendEmailDialog" :boarder-list="currentSelection" @close="onSendEmailDialogClose" @send="onSendEmailDialogClose")
    v-container(style="padding: 12px 0")
      v-card-title(style=" padding: 25px 0") {{ $t('debtorsPage.pageTitle') }}
      v-toolbar(dense flat)
        v-btn.pl-0(small text  @click="onSendEmailButtonClick" :disabled="isSendEmailButtonDisabled")
          v-icon fas fa-envelope
          span.ma-2 {{ $t('sendEmailDialog.createNewMessageLabel') }} {{ selectionCount }}
    v-container.pa-0.flex-shrink-0.flex-grow-1
      AgGridVue.mt-2.ag-theme-balham(style="width: 100%; height: 100%; min-height: 500px;"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :pinnedBottomRowData="pinnedRowData"
        :localeTextFunc="localeTextFunc"
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChange"
        @row-double-clicked="onDebtorRowDoubleClick")
</template>

<script lang="ts">
import Vue from "vue";
import {Component} from "vue-property-decorator";
import {AgGridVue} from "ag-grid-vue";
import DebtorDetailDialog from "@/components/debtor/DebtorDetailDialog.vue";
import {
  ColDef,
  ColumnApi,
  GridApi,
  GridOptions,
  ICellRendererParams,
  RowClickedEvent, RowNode, ValueFormatterParams,
  ValueGetterParams
} from "ag-grid-community";
import {Debt} from "@/model/debtors/Debt";
import {formatIntPrice} from "@/services/formatter";
import {Action} from "vuex-class";
import {FETCH_DEBTORS} from "@/store/action-types";
import SendEmailDialog from "@/components/boarder-management/SendEmailDialog.vue";

@Component({
  components: {
    AgGridVue,
    DebtorDetailDialog,
    SendEmailDialog
  }
})

export default class DebtorsPage extends Vue {

  private gridOptions: GridOptions = {}
  // noinspection JSMismatchedCollectionQueryUpdate
  private columnDefs: ColDef[] = []
  // noinspection JSMismatchedCollectionQueryUpdate
  private rowData: Debt[] = []
  private gridApi: GridApi | null = null
  private columnApi: ColumnApi | null = null
  private selectedDebtor: Debt | null = null
  // noinspection JSMismatchedCollectionQueryUpdate
  private currentSelection: any[] = []
  private pinnedRowData: any = []

  private showDebtorDetailDialog: boolean = false

  private snackbar: boolean = false
  private snackbarMessage: string = ''
  private snackbarColor: string = ''

  private localeTextFunc: Function | null = null

  private debtorsList: Debt[] = []
  private showSendEmailDialog: boolean = false

  @Action(FETCH_DEBTORS) fetchDebtors!: { (): Promise<Debt[]> }

  get isSendEmailButtonDisabled(): boolean {
    return this.currentSelection.length === 0
  }

  calculatePinnedRowData(dataset: Debt[]): any[] {
    let totalUnpaidBillingsAmount = 0
    let totalDebtSum = 0

    dataset.forEach(i => {
      totalUnpaidBillingsAmount += i.debtOverview.unpaidInvoiceCount
      totalDebtSum += i.debtOverview.totalAmountToPay
    })

    return [{
      boarderGroup: null,
      boarderName: null,
      unpaidInvoiceCount: totalUnpaidBillingsAmount,
      formattedDebtSum: formatIntPrice(totalDebtSum)
    }];
  }

  onSendEmailButtonClick (): void {
    this.showSendEmailDialog = true
  }

  onSendEmailDialogClose (): void {
    this.showSendEmailDialog = false
  }

  async fetchDebtorsList(): Promise<void> {

    setTimeout(() => {this.gridOptions.api?.showLoadingOverlay()}, 0)

    try {
      this.debtorsList = await this.fetchDebtors()
      this.rowData = this.debtorsList
      this.pinnedRowData = this.calculatePinnedRowData(this.rowData)

    } catch (e) {
      this.snackbar = true
      this.snackbarMessage = this.$t('errors.fetchDataError').toString()
      this.snackbarColor = "alert"
    }
    finally {
      this.gridOptions.api?.hideOverlay()
    }
  }

  onClearField () {

  }

  get selectionCount(): string {
    const count = this.currentSelection.length

    if (count > 0) {
      return `(${count})`
    }

    return ''
  }

  onGridReady(params: ICellRendererParams) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

  onSelectionChange(params: GridOptions): void {
    this.currentSelection = params.api!.getSelectedRows()
  }

  onDebtorRowDoubleClick(params: RowClickedEvent): void {
    this.selectedDebtor = params.data
    this.showDebtorDetailDialog = true
  }

  closeDebtorDetailDialog(): void {
    this.showDebtorDetailDialog = false
  }

  createGridOptions(): GridOptions {
    return {
      rowSelection: 'multiple',
      rowDeselection: true,
      rowMultiSelectWithClick: false,
      suppressContextMenu: true,

      getRowClass(params: any): string {
        if (params.node.rowPinned) {
          return ''
        } else {
          return params.data.status.name === 'active' ? '' : 'gray-background-row'
        }
      },

      defaultColDef: {
        filter: true,
        sortable: true,
        enableCellChangeFlash: true,
        floatingFilter: true,
        menuTabs: []
      },

      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
        ],
      },

      cellFadeDelay: 1500,

      onFilterChanged: (params: any) => {
        const dataset = params.api.rowModel.rowsToDisplay.map((r: RowNode) => r.data)

        setTimeout(() => {
          params.api.setPinnedBottomRowData(this.calculatePinnedRowData(dataset))
        }, 0)
      },

      overlayLoadingTemplate:
          `<div class="ag-overlay-loading-center" style="font-size: 15px">
               <i class="fas fa-spinner fa-pulse"></i>
               <span style="padding-left: 10px"> ${this.$t('debtorsPage.loadingGridDataText')} </span>
           </div>`
    }
  }

  createColDefs(): ColDef[] {
    return [
      {
        headerName: this.$t('debtorsPage.boarderGroupColumnTitle').toString(),
        filter: 'agTextColumnFilter',
        filterParams: {
          debounceMs: 250,
          textFormatter: (value: string) => value.toString().normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
        },
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        width: 150,

        valueGetter(params: ValueGetterParams): string {
          if (params.node.rowPinned) {
            return ""
          } else {
            let text: string = ''
            params.data.boarderGroupCollection.forEach((item: any, index: number) => {
              text += index === 0 ? item.name : `, ${item.name}`
            })
            return text
          }
        }
      },
      {
        headerName: this.$t('debtorsPage.boarderNameColumnTitle').toString(),
        filter: 'agTextColumnFilter',
        filterParams: {
          debounceMs: 250,
          textCustomComparator: (filter: string, value: string, filterText: string) => {
            const filterTextNormalizeLowerCase = filterText.normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const valueNormalizeLowerCase = value.toString().normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '').toLowerCase();


            switch (filter) {
              case 'contains':
                let passed = true
                filterTextNormalizeLowerCase.split(' ').forEach((filterWord: string) => {
                  if (valueNormalizeLowerCase.indexOf(filterWord) < 0) {
                    passed = false;
                  }
                })
                return passed;
              default:
                return false;
            }
          }
        },
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        flex: 1,
        minWidth: 221,

        valueGetter(params: ValueGetterParams): string {
          if (params.node.rowPinned) {
            return ""
          } else {
            return `${params.data.person.familyName} ${params.data.person.firstName}`
          }
        }
      },
      {
        headerName: this.$t('debtorsPage.unpaidBillingsCountColumnTitle').toString(),
        field: 'unpaidInvoiceCountNatural',
        filter: 'agNumberColumnFilter',
        width: 195,
        cellStyle: {display: 'flex', 'justify-content': 'end'},
        valueFormatter(params: ValueFormatterParams): string {
          return (params.data as Debt).unpaidInvoiceCount
        }
      },
      {
        headerName: this.$t('debtorsPage.debtSumColumnTitle').toString() + ' €',
        field: 'totalAmountToPayNatural',
        filter: 'agNumberColumnFilter',
        width: 150,
        cellStyle: {display: 'flex', 'justify-content': 'end'},
        valueFormatter(params: ValueFormatterParams): string {
          return (params.data as Debt).formattedDebtSum + ' €'
        }
      }
    ]
  }

  beforeMount(): void {
    this.gridOptions = this.createGridOptions()
    this.columnDefs = this.createColDefs()
  }

  created () {
    this.localeTextFunc = (key: string, defaultValue: string) => {
      const tKey = `agGrid.${key}`
      const translated = this.$t(tKey)
      if(tKey === translated){
        return defaultValue
      } else {
        return translated
      }

    }
  }

  mounted(): void {
    this.fetchDebtorsList()
  }
}
</script>

<style lang="stylus" scoped>

>>> .ag-text-field-input {
  height 25px
}

>>> .gray-background-row {
  background-color grey !important
}

</style>